import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { textFieldDarkSx } from '../components/dialogDarkGlass';
import { MAIN_BG_COLOR } from '../theme';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { getPodscopeBrochureContactTemplateId, sendContactEmail } from '../services/contactEmail';

const CONTACT_SOURCE = 'Podscope brochure';

const DEFAULT_PODSCOPE_BROCHURE_PDF_URL =
    'https://podscope.s3.us-east-1.amazonaws.com/legal/Brochure.pdf';

const brochurePdfUrl =
    process.env.REACT_APP_PODSCOPE_BROCHURE_PDF_URL?.trim() || DEFAULT_PODSCOPE_BROCHURE_PDF_URL;

const alertSuccessSx = {
    mb: 2,
    bgcolor: 'rgba(76, 175, 80, 0.18)',
    color: '#c8e6c9',
    border: '1px solid rgba(129, 199, 132, 0.45)',
    '& .MuiAlert-icon': { color: '#a5d6a7' },
};

const alertErrorSx = {
    mb: 2,
    bgcolor: 'rgba(244, 67, 54, 0.15)',
    color: '#ffcdd2',
    border: '1px solid rgba(239, 83, 80, 0.45)',
    '& .MuiAlert-icon': { color: '#ef9a9a' },
};

const PodscopeBrochurePage: React.FC = () => {
    const theme = useTheme();
    const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await sendContactEmail({
                name,
                email,
                message,
                templateId: getPodscopeBrochureContactTemplateId(),
            });
            setSuccess(true);
            analytics.track(AnalyticsEvent.SUBMIT_CONTACT_FORM, {
                source: CONTACT_SOURCE,
            });
            setName('');
            setEmail('');
            setMessage('');
            window.setTimeout(() => setSuccess(false), 4500);
        } catch (err) {
            setError('Error al enviar el mensaje. Por favor intenta nuevamente.');
            console.error('EmailJS error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                bgcolor: MAIN_BG_COLOR,
                minHeight: { xs: 'calc(100dvh - 56px)', md: 'calc(100dvh - 64px)' },
                py: { xs: 3, sm: 4 },
                px: { xs: 2, sm: 3 },
                boxSizing: 'border-box',
            }}
        >
            <Container maxWidth="lg" disableGutters sx={{ width: '100%', mx: 'auto' }}>
                <Stack spacing={{ xs: 4, sm: 5 }}>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                color: 'white',
                                fontSize: { xs: '2rem', sm: '2.375rem' },
                                lineHeight: 1.15,
                                mb: 1,
                            }}
                        >
                            Contacto
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                color: 'rgba(255, 255, 255, 0.75)',
                                lineHeight: 1.5,
                            }}
                        >
                            El brochure de Podscope está más abajo.
                        </Typography>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            p: { xs: 2.5, sm: 3 },
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.14)',
                            bgcolor: 'rgba(10, 12, 18, 0.55)',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                color: 'white',
                                mb: 2,
                                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                            }}
                        >
                            Escríbenos
                        </Typography>

                        {success && (
                            <Alert severity="success" sx={alertSuccessSx}>
                                ¡Mensaje enviado con éxito! Te contactaremos pronto.
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" sx={alertErrorSx}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            required
                            fullWidth
                            label="Nombre"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            disabled={loading || success}
                            sx={{ mb: 2, ...textFieldDarkSx }}
                        />
                        <TextField
                            required
                            fullWidth
                            type="email"
                            label="Email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={loading || success}
                            sx={{ mb: 2, ...textFieldDarkSx }}
                        />
                        <TextField
                            required
                            fullWidth
                            multiline
                            rows={4}
                            label="Mensaje"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            disabled={loading || success}
                            placeholder="¿En qué podemos ayudarte?"
                            sx={{ mb: 2, ...textFieldDarkSx }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || success || !name || !email || !message}
                            endIcon={
                                loading ? (
                                    <CircularProgress size={18} sx={{ color: 'white' }} />
                                ) : (
                                    <SendIcon />
                                )
                            }
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.14)',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.22)',
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.25,
                                borderRadius: '10px',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.22)',
                                    boxShadow: 'none',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'rgba(255,255,255,0.06)',
                                    color: 'rgba(255,255,255,0.35)',
                                    borderColor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            {loading ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Box>

                    {brochurePdfUrl ? (
                        <Stack spacing={1.5}>
                            <Typography
                                variant="subtitle1"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    fontFamily: '"DM Serif Display", serif',
                                    color: 'white',
                                    fontSize: { xs: '1.15rem', sm: '1.3rem' },
                                }}
                            >
                                Brochure
                            </Typography>
                            {isMobileLayout && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.65)',
                                        lineHeight: 1.45,
                                        mb: 0.5,
                                    }}
                                >
                                    En el teléfono, ábrelo con el botón para verlo a pantalla
                                    completa.
                                </Typography>
                            )}
                            <Button
                                component="a"
                                href={brochurePdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                fullWidth
                                variant="outlined"
                                size="large"
                                startIcon={<PictureAsPdfIcon />}
                                sx={{
                                    display: { xs: 'inline-flex', md: 'none' },
                                    color: 'white',
                                    borderColor: 'rgba(255,255,255,0.35)',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    py: 1.5,
                                    '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.55)',
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                    },
                                }}
                            >
                                Ver brochure
                            </Button>
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255, 255, 255, 0.14)',
                                    bgcolor: 'rgba(10, 12, 18, 0.55)',
                                }}
                            >
                                <Box
                                    component="iframe"
                                    title="Brochure Podscope"
                                    src={brochurePdfUrl}
                                    sx={{
                                        display: 'block',
                                        width: '100%',
                                        height: {
                                            xs: 'min(85vh, 720px)',
                                            md: '70vh',
                                        },
                                        border: 'none',
                                        bgcolor: 'rgba(0,0,0,0.25)',
                                    }}
                                />
                            </Box>
                        </Stack>
                    ) : (
                        <Typography
                            sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.95rem' }}
                        >
                            PDF no disponible.
                        </Typography>
                    )}
                </Stack>
            </Container>
        </Box>
    );
};

export default PodscopeBrochurePage;
